import { supabase } from '../lib/supabase';

export const getReferees = async () => {
  const { data, error } = await supabase
    .from('referees')
    .select(`
      *,
      department:departments(
        department_id,
        name,
        name_tamil
      )
    `)
    .order('last_name', { ascending: true });

  if (error) throw error;
  return data;
};

export const getRefereeById = async (id: string) => {
  const { data, error } = await supabase
    .from('referees')
    .select(`
      *,
      department:departments(
        department_id,
        name,
        name_tamil
      )
    `)
    .eq('referee_id', id)
    .single();

  if (error) throw error;
  return data;
};

export const createReferee = async (referee: any) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can create referees');
  }

  const { data, error } = await supabase
    .from('referees')
    .insert([referee])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const updateReferee = async (id: string, referee: any) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update referees');
  }

  const { data, error } = await supabase
    .from('referees')
    .update(referee)
    .eq('referee_id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const checkRefereeAssignments = async (id: string) => {
  const { data, error } = await supabase
    .from('event_referees')
    .select('assignment_id')
    .eq('referee_id', id);

  if (error) throw error;
  return data.length > 0;
};

export const deleteReferee = async (id: string) => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete referees');
  }

  // First check if referee has any assignments
  const hasAssignments = await checkRefereeAssignments(id);
  if (hasAssignments) {
    throw new Error('Cannot delete referee: They are assigned to one or more competitions');
  }

  const { error } = await supabase
    .from('referees')
    .delete()
    .eq('referee_id', id);

  if (error) throw error;
};

export const checkUserCanManageReferees = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  ) ?? false;
};